import { z } from "zod";

import type { ExportMappingRule, ExportMatch } from "@/lib/types/itsm-config";

// The conditions an export rule can narrow its desk by - catalog items and a
// custom field value - and "Don't sync". The backend resolves them the same
// way for every provider (`itsm_integration_core.config_resolver`).

/** Form fields for the conditions; spread into a rule form's zod object. */
export const conditionFormShape = {
  service_catalog_item_ids: z.array(z.string()),
  custom_field_name: z.string().trim().optional(),
  custom_field_values: z.array(z.string()),
  exclude: z.boolean(),
};

export interface ConditionValues {
  service_catalog_item_ids: string[];
  custom_field_name?: string;
  custom_field_values: string[];
  exclude: boolean;
}

export const EMPTY_CONDITIONS: ConditionValues = {
  service_catalog_item_ids: [],
  custom_field_values: [],
  exclude: false,
};

/** A custom field condition needs at least one value; for `.refine`. */
export function hasConditionValues(v: ConditionValues): boolean {
  return !v.custom_field_name || v.custom_field_values.length > 0;
}

export function conditionsToForm(rule: ExportMappingRule): ConditionValues {
  return {
    service_catalog_item_ids: rule.match.service_catalog_item_ids ?? [],
    custom_field_name: rule.match.custom_field?.name ?? "",
    custom_field_values: rule.match.custom_field?.values ?? [],
    exclude: Boolean(rule.exclude),
  };
}

/** The match fields the conditions set; empty ones are saved as null. */
export function conditionsMatch(
  v: ConditionValues,
): Pick<ExportMatch, "service_catalog_item_ids" | "custom_field"> {
  return {
    service_catalog_item_ids: v.service_catalog_item_ids.length
      ? v.service_catalog_item_ids
      : null,
    custom_field: v.custom_field_name
      ? { name: v.custom_field_name, values: v.custom_field_values }
      : null,
  };
}
